var _ = require("../../../uam/module.js")(module.exports, {
  namespace: "ua.file.upload",
  title: "File Upload Control",
  description: "Resumable chunked uploader with queue support for Universe file routes.",
  control: true,
  supportServer: false,
  fields: {
    name: { title: "Name", type: "string", description: "Control name.", required: true },
    scope: { title: "Scope", type: "string", description: "Upload scope: content or secured.", default: "content" },
    folder: { title: "Folder", type: "string", description: "Relative folder path under scope.", default: "" },
    "allow-scope-toggle": { title: "Allow Scope Toggle", type: "boolean", description: "Legacy option. Scope is now controlled by privacy toggle.", default: false },
    "allow-folder": { title: "Allow Folder", type: "boolean", description: "Legacy option. Folder is now provided by parent context.", default: false },
    "show-preview": { title: "Show Preview", type: "boolean", description: "Show queue and item statuses.", default: true },
    "status-target": { title: "Status Target", type: "string", description: "Element id to print status text into." },
    allowmultiplefiles: { title: "Allow Multiple Files", type: "boolean", description: "Allow selecting multiple files per action.", default: false },
    allowfolders: { title: "Allow Folders", type: "boolean", description: "Allow folder uploads.", default: false },
    allowdragdropfiles: { title: "Allow Drag and Drop Files", type: "boolean", description: "Detect file drags over the window and support drop on this uploader.", default: true },
    allowwindowdropfiles: { title: "Allow Window Drop Files", type: "boolean", description: "When true and only one uploader exists, document/window drops are accepted by this uploader.", default: false },
    continuouswork: { title: "Continuous Work", type: "boolean", description: "Allow continuing to add files while queue processes.", default: false },
    "show-secure-toggle": { title: "Show Secure Toggle", type: "boolean", description: "Legacy option. Privacy toggle is always available.", default: true },
    maxSecureLinkExpiry: { title: "Max Secure Link Expiry", type: "string", description: "Maximum secure-link expiry option shown in ua.access.", default: "none" }
  }
});

var jsonRender = require("../interface/jsonRender.js");
var uai = require("../interface/interface.js");

var DEFAULT_CHUNK = 1024 * 1024;

function getValueBool(options, key, fallback) {
  if (!(key in options)) return fallback;
  return options[key] === true || options[key] === "true";
}

function sanitizeFolder(value) {
  var raw = (value || "").toString().trim().replace(/\\/g, "/");
  raw = raw.replace(/^\/+/, "").replace(/\/+$/, "");
  if (!(raw)) return "";

  var parts = raw.split("/").filter(Boolean);
  var next = [];
  for (var i = 0; i < parts.length; i++) {
    if (parts[i] === "." || parts[i] === "..") continue;
    next.push(parts[i].replace(/[^a-zA-Z0-9._-]/g, ""));
  }
  return next.filter(Boolean).join("/");
}

function joinFolder(base, relative) {
  var b = sanitizeFolder(base);
  var r = sanitizeFolder(relative);
  if (b && r) return b + "/" + r;
  return b || r || "";
}

function inferRelativeFolder(file) {
  var rel = (file && file.webkitRelativePath) ? file.webkitRelativePath.toString() : "";
  if (!(rel)) return "";
  var idx = rel.lastIndexOf("/");
  if (idx <= 0) return "";
  return sanitizeFolder(rel.slice(0, idx));
}

function hasFilePayload(event) {
  var dt = event && event.dataTransfer;
  if (!(dt)) return false;
  if (dt.files && dt.files.length > 0) return true;
  var types = dt.types || [];
  for (var i = 0; i < types.length; i++) {
    if ((types[i] || "").toString() === "Files") return true;
  }
  return false;
}

function buildItemStatusLabel(item) {
  if (item.status === "queued") return "Queued";
  if (item.status === "uploading") return "Uploading";
  if (item.status === "paused") return "Paused";
  if (item.status === "lost") return "Connection lost";
  if (item.status === "cancelled") return "Cancelled";
  if (item.status === "complete") return "Complete";
  if (item.status === "error") return "Failed";
  return "Queued";
}

function newQueueItem(file, baseFolder, isSecure, scopeValue) {
  var relativeFolder = inferRelativeFolder(file);
  var finalFolder = joinFolder(baseFolder, relativeFolder);
  return {
    id: uai.generateRandomID(12),
    file: file,
    secure: isSecure === true,
    scope: isSecure ? "secured" : scopeValue,
    folder: finalFolder,
    status: "queued",
    message: "Queued",
    progressPct: 0,
    uploadId: "",
    chunkMaxBytes: DEFAULT_CHUNK,
    totalChunks: 0,
    receivedChunks: [],
    pauseRequested: false,
    cancelled: false,
    record: null
  };
}

async function setStatus(options, message, isError) {
  var hostId = (options["status-target"] || "").toString().trim();
  if (!(hostId)) return;

  var host = document.getElementById(hostId);
  if (!(host)) return;

  var msg = (message || "").toString().trim();
  host.className = (isError ? "small text-danger mt-2" : "small text-muted mt-2") + (msg ? "" : " d-none");
  host.textContent = msg;
}

function isConnectionError(error) {
  var text = ((error && error.message) || "").toString().toLowerCase();
  if (text.includes("network")) return true;
  if (text.includes("fetch")) return true;
  if (text.includes("failed to fetch")) return true;
  if (text.includes("connection")) return true;
  return false;
}

async function httpJson(url, options) {
  var repo = await fetch(url, options || {});
  var result = null;
  try {
    result = await repo.json();
  } catch (parseError) {
    var bodyText = "";
    try {
      bodyText = await repo.text();
    } catch (readError) {
    }
    throw new Error("Request failed (" + repo.status + "): " + (bodyText || parseError.message || "Unable to parse response."));
  }
  if (!(result && result.success)) {
    var err = (result && result.errors && result.errors[0]) || { description: "Request failed." };
    throw new Error(err.description || err.title || "Request failed.");
  }
  return result.data;
}

async function showSecureDialog(record, options) {
  if (!(record) || !(record.id)) return;
  if (!(window && window.UATools && typeof window.UATools.render === "function")) return;

  var dialogJson = {
    n: "ua.dialog",
    title: "Secure File Settings",
    allowclose: true,
    inner: {
      n: "div",
      c: "dashboard-wizard-container",
      i: {
        n: "ua.wizard",
        name: "/files/access/" + encodeURIComponent(record.id),
        showSummaryStep: false,
        inner: [
          {
            n: "div",
            "data-wizard-title": "Access",
            i: [
              { n: "p", c: "small text-muted", i: "Configure secure access for " + (record.displayName || record.originalName || record.id) + "." },
              {
                n: "ua.access",
                name: "access",
                title: "Security Mode",
                policy: "role",
                requiredRole: "editor",
                maxSecureLinkExpiry: options.maxSecureLinkExpiry || "none"
              }
            ]
          }
        ]
      }
    }
  };

  var rendered = window.UATools.render(dialogJson);
  if (!(rendered) || !(rendered.nodeType)) return;
  document.body.appendChild(rendered);

  function closeDialog() {
    if (rendered && rendered.parentNode) rendered.parentNode.removeChild(rendered);
  }

  var closeTimer = setInterval(function() {
    var successMarker = rendered.querySelector(".alert-success,.ua-wizard-success,.wizard-success");
    if (successMarker) {
      clearInterval(closeTimer);
      setTimeout(closeDialog, 500);
    }
  }, 350);

  setTimeout(function() {
    clearInterval(closeTimer);
  }, 30000);
}

async function readDirectoryFiles(handle, basePath) {
  var files = [];
  var prefix = (basePath || "").toString();

  for await (var entry of handle.values()) {
    var nextPath = prefix ? (prefix + "/" + entry.name) : entry.name;
    if (entry.kind === "file") {
      var file = await entry.getFile();
      try {
        Object.defineProperty(file, "webkitRelativePath", {
          configurable: true,
          enumerable: false,
          writable: true,
          value: nextPath
        });
      } catch (error) {
        file.webkitRelativePath = nextPath;
      }
      files.push(file);
    } else if (entry.kind === "directory") {
      var nested = await readDirectoryFiles(entry, nextPath);
      for (var i = 0; i < nested.length; i++) files.push(nested[i]);
    }
  }
  return files;
}

exports.render = async function(options) {
  var scopeDefault = (options.scope || "content").toString().trim().toLowerCase();
  if (!(scopeDefault === "content" || scopeDefault === "secured")) scopeDefault = "content";
  var folderDefault = sanitizeFolder(options.folder || "");

  var allowMultipleFiles = getValueBool(options, "allowmultiplefiles", false);
  var allowFolders = getValueBool(options, "allowfolders", false);
  var allowDragDropFiles = getValueBool(options, "allowdragdropfiles", true);
  var allowWindowDropFiles = getValueBool(options, "allowwindowdropfiles", false);
  var showPreview = getValueBool(options, "show-preview", true);
  var continuousWork = getValueBool(options, "continuouswork", getValueBool(options, "continouswork", false));

  var html = {
    n: "div",
    c: "ua-file-upload-control d-flex flex-column gap-2 position-relative",
    "data-ua-file-upload": "true",
    "data-ua-file-upload-name": options.name,
    "data-upload-current-folder": folderDefault,
    "data-upload-privacy": scopeDefault === "secured" ? "secure" : "public",
    style: "border-radius:0.75rem;",
    i: [
      {
        n: "div",
        c: "card border rounded-3 p-2",
        "data-upload-picker-wrap": "true",
        i: {
          n: "div",
          c: "d-flex align-items-center gap-2 flex-nowrap",
          i: [
            {
              n: "button",
              type: "button",
              c: "btn btn-outline-secondary btn-sm",
              "data-upload-privacy-toggle": "true",
              title: "Toggle privacy",
              "aria-label": "Upload privacy is public",
              i: { n: "i", c: "bi bi-globe", "data-upload-privacy-icon": "true" }
            },
            {
              n: "button",
              type: "button",
              c: "btn btn-primary btn-sm flex-grow-1 text-start text-truncate",
              "data-upload-select-main": "true",
              i: "Upload Files or Folders"
            }
          ]
        }
      },
      { n: "input", type: "hidden", value: scopeDefault, "data-upload-scope": "true" },
      { n: "input", type: "hidden", value: folderDefault, "data-upload-folder": "true" },
      { n: "input", type: "file", "data-upload-file": "true", style: "display:none;", multiple: allowMultipleFiles ? true : undefined },
      allowFolders
        ? { n: "input", type: "file", "data-upload-folder-input": "true", style: "display:none;", multiple: true, webkitdirectory: true, directory: true }
        : { n: "span", c: "d-none", "data-upload-folder-input": "true", i: "" },
      showPreview
        ? { n: "div", c: "d-flex flex-column gap-2", "data-upload-queue": "true", i: [] }
        : { n: "div", c: "d-none", "data-upload-queue": "true", i: [] },
      { n: "div", c: "small text-muted", "data-upload-message": "true", i: "" },
      {
        n: "div",
        c: "position-absolute top-0 start-0 end-0 bottom-0 d-none",
        "data-upload-overlay": "true",
        style: "border-radius:0.75rem;background:rgba(13,110,253,0.08);border:0.15rem dashed rgba(13,110,253,0.75);backdrop-filter:blur(0.05rem);align-items:center;justify-content:center;pointer-events:none;z-index:5;",
        i: {
          n: "div",
          c: "fw-semibold text-primary",
          style: "transform:scale(0.96);transition:transform 180ms ease;",
          "data-upload-overlay-text": "true",
          i: "Drop files here to upload"
        }
      }
    ]
  };

  var dom = await jsonRender.render(html);

  var pickerWrap = dom.querySelector("[data-upload-picker-wrap]");
  var fileInput = dom.querySelector("[data-upload-file]");
  var folderInput = dom.querySelector("[data-upload-folder-input]");
  var scopeInput = dom.querySelector("[data-upload-scope]");
  var folderPathInput = dom.querySelector("[data-upload-folder]");
  var selectMainBtn = dom.querySelector("[data-upload-select-main]");
  var privacyToggleBtn = dom.querySelector("[data-upload-privacy-toggle]");
  var privacyIcon = dom.querySelector("[data-upload-privacy-icon]");
  var queueHost = dom.querySelector("[data-upload-queue]");
  var message = dom.querySelector("[data-upload-message]");
  var overlay = dom.querySelector("[data-upload-overlay]");
  var overlayText = dom.querySelector("[data-upload-overlay-text]");

  var dragState = {
    hoverOnUploader: false
  };

  var state = {
    items: [],
    processing: false,
    secure: scopeDefault === "secured"
  };

  function updatePrivacyVisual() {
    var isSecure = state.secure === true;
    if (scopeInput) scopeInput.value = isSecure ? "secured" : "content";
    dom.setAttribute("data-upload-privacy", isSecure ? "secure" : "public");

    if (privacyIcon) {
      privacyIcon.className = isSecure ? "bi bi-lock-fill" : "bi bi-globe";
    }
    if (privacyToggleBtn) {
      privacyToggleBtn.className = isSecure ? "btn btn-outline-warning btn-sm" : "btn btn-outline-secondary btn-sm";
      privacyToggleBtn.setAttribute("aria-label", isSecure ? "Upload privacy is secure" : "Upload privacy is public");
      privacyToggleBtn.setAttribute("title", isSecure ? "Secure uploads" : "Public uploads");
    }
  }

  function updateOverlayVisible(isVisible) {
    if (!(overlay)) return;
    overlay.classList.toggle("d-none", !isVisible);
    if (overlayText) {
      overlayText.style.transform = isVisible ? "scale(1)" : "scale(0.96)";
    }
  }

  function setCurrentFolder(nextFolder) {
    var sanitized = sanitizeFolder(nextFolder || "");
    if (folderPathInput) folderPathInput.value = sanitized;
    dom.setAttribute("data-upload-current-folder", sanitized);
  }

  function setMessage(text, isError) {
    if (!(message)) return;
    var msg = (text || "").toString().trim();
    message.className = (isError ? "small text-danger" : "small text-muted") + (msg ? "" : " d-none");
    message.textContent = msg;
    setStatus(options, msg, isError);
  }

  function queueStats() {
    var uploading = 0;
    var complete = 0;
    var failed = 0;
    for (var i = 0; i < state.items.length; i++) {
      if (state.items[i].status === "uploading") uploading += 1;
      if (state.items[i].status === "complete") complete += 1;
      if (state.items[i].status === "error" || state.items[i].status === "lost") failed += 1;
    }
    return { uploading: uploading, complete: complete, failed: failed, total: state.items.length };
  }

  function renderQueue() {
    if (!(queueHost)) return;
    queueHost.innerHTML = "";

    var stats = queueStats();
    if (pickerWrap) {
      var hasLiveUploads = stats.uploading > 0;
      pickerWrap.classList.toggle("d-none", hasLiveUploads && !continuousWork);
    }

    if (state.items.length === 0 || !showPreview) return;

    for (var i = 0; i < state.items.length; i++) {
      var item = state.items[i];

      var row = document.createElement("div");
      row.className = "d-flex align-items-center gap-2";
      row.setAttribute("data-upload-item-id", item.id);

      var controls = document.createElement("div");
      controls.className = "d-flex gap-1";

      if (item.status === "uploading" || item.status === "paused" || item.status === "lost") {
        var cancelBtn = document.createElement("button");
        cancelBtn.type = "button";
        cancelBtn.className = "btn btn-outline-danger btn-sm";
        cancelBtn.setAttribute("data-upload-item-action", "cancel");
        cancelBtn.setAttribute("data-upload-item-id", item.id);
        cancelBtn.innerHTML = "<i class='bi bi-x-lg'></i>";
        controls.appendChild(cancelBtn);
      }

      if (item.status === "uploading") {
        var pauseBtn = document.createElement("button");
        pauseBtn.type = "button";
        pauseBtn.className = "btn btn-outline-secondary btn-sm";
        pauseBtn.setAttribute("data-upload-item-action", "pause");
        pauseBtn.setAttribute("data-upload-item-id", item.id);
        pauseBtn.innerHTML = "<i class='bi bi-pause-fill'></i>";
        controls.appendChild(pauseBtn);
      }

      if (item.status === "paused" || item.status === "lost") {
        var resumeBtn = document.createElement("button");
        resumeBtn.type = "button";
        resumeBtn.className = "btn btn-outline-primary btn-sm";
        resumeBtn.setAttribute("data-upload-item-action", "resume");
        resumeBtn.setAttribute("data-upload-item-id", item.id);
        resumeBtn.innerHTML = "<i class='bi bi-play-fill'></i>";
        controls.appendChild(resumeBtn);
      }

      var body = document.createElement("div");
      body.className = "flex-grow-1";

      var title = document.createElement("div");
      title.className = "small";
      if (item.status === "lost") {
        title.textContent = "Connection lost while uploading " + (item.file.name || "file") + ". Resume upload?";
      } else if (item.status === "error") {
        title.textContent = "Failed • " + (item.file.name || "file") + (item.message ? (" • " + item.message) : "");
      } else {
        title.textContent = buildItemStatusLabel(item) + " • " + (item.file.name || "file");
      }

      var bar = document.createElement("div");
      bar.className = "progress";
      bar.style.height = "0.5rem";

      var barInner = document.createElement("div");
      barInner.className = "progress-bar";
      if (item.status === "complete") barInner.className += " bg-success";
      if (item.status === "lost" || item.status === "error") barInner.className += " bg-danger";
      barInner.style.width = Math.max(0, Math.min(100, Number(item.progressPct || 0))) + "%";
      barInner.setAttribute("role", "progressbar");
      barInner.setAttribute("aria-valuenow", Math.round(item.progressPct || 0).toString());
      barInner.setAttribute("aria-valuemin", "0");
      barInner.setAttribute("aria-valuemax", "100");
      bar.appendChild(barInner);

      body.appendChild(title);
      body.appendChild(bar);

      row.appendChild(controls);
      row.appendChild(body);
      queueHost.appendChild(row);
    }

    setMessage("Queue: " + stats.total + " • Uploading: " + stats.uploading + " • Complete: " + stats.complete + " • Issues: " + stats.failed, stats.failed > 0);
  }

  function currentScope() {
    var scope = (scopeInput && scopeInput.value) ? scopeInput.value : (state.secure ? "secured" : "content");
    scope = (scope || "content").toString().trim().toLowerCase();
    if (!(scope === "content" || scope === "secured")) scope = "content";
    return scope;
  }

  function addFilesToQueue(fileList) {
    if (!(fileList) || fileList.length === 0) return;

    var filesToAdd = [];
    for (var i = 0; i < fileList.length; i++) {
      if (fileList[i] && fileList[i].size >= 0) filesToAdd.push(fileList[i]);
    }
    if (filesToAdd.length === 0) return;

    if (!(allowMultipleFiles) && filesToAdd.length > 1) filesToAdd = [filesToAdd[0]];
    if (!(allowMultipleFiles) && state.items.length > 0) state.items = [];

    var baseFolder = folderPathInput ? (folderPathInput.value || "") : "";
    var scope = currentScope();
    var secureFlag = scope === "secured";

    for (var j = 0; j < filesToAdd.length; j++) {
      state.items.push(newQueueItem(filesToAdd[j], baseFolder, secureFlag, scope));
    }

    renderQueue();
    processQueue();
  }

  async function requestUploadSession(item) {
    var chunkGuess = item.chunkMaxBytes || DEFAULT_CHUNK;
    var totalChunks = Math.max(1, Math.ceil(item.file.size / chunkGuess));

    var data = await httpJson("/files/upload/session", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        scope: item.scope,
        folder: item.folder,
        originalName: item.file.name,
        mimeType: item.file.type || "application/octet-stream",
        size: item.file.size,
        totalChunks: totalChunks
      })
    });

    item.uploadId = data.uploadId;
    item.chunkMaxBytes = Number(data.chunkMaxBytes || DEFAULT_CHUNK);
    item.totalChunks = Math.max(1, Math.ceil(item.file.size / item.chunkMaxBytes));
    item.receivedChunks = [];

    var warning = (data && data.warning) || null;
    if (warning && warning.type === "overwrite") {
      var folderLabel = item.folder ? ("/" + item.folder) : "/";
      var proceed = window.confirm(
        "A file named '" + item.file.name + "' already exists in " + folderLabel + ". " +
        "Continue to overwrite the file content and keep the existing metadata (display name, tags, description)?"
      );

      if (!proceed) {
        try {
          await httpJson("/files/upload/cancel/" + encodeURIComponent(item.uploadId), {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: "{}"
          });
        } catch (cancelError) {
          console.warn("ua.file.upload overwrite warning cancel cleanup failed", cancelError);
        }
        item.cancelled = true;
        item.status = "cancelled";
        item.message = "Upload cancelled by user before overwrite.";
        throw new Error("Upload cancelled before overwrite.");
      }
    }
  }

  async function syncUploadStatus(item) {
    if (!(item.uploadId)) return;
    try {
      var session = await httpJson("/files/upload/status/" + encodeURIComponent(item.uploadId), {
        method: "GET",
        headers: { "Content-Type": "application/json" }
      });

      item.totalChunks = Number(session.totalChunks || item.totalChunks || 0) || item.totalChunks;
      item.receivedChunks = (session.receivedChunks instanceof Array)
        ? session.receivedChunks.slice().sort(function(a, b) { return a - b; })
        : [];

      if (item.totalChunks > 0) {
        item.progressPct = Math.min(100, (item.receivedChunks.length / item.totalChunks) * 100);
      }
    } catch (error) {
      console.warn("ua.file.upload: unable to sync upload status.", error);
    }
  }

  async function uploadChunk(item, index, chunk) {
    var data = await httpJson("/files/upload/chunk/" + encodeURIComponent(item.uploadId) + "/" + encodeURIComponent(index), {
      method: "PUT",
      headers: {
        "Content-Type": "application/octet-stream"
      },
      body: chunk
    });

    item.receivedChunks = (data.receivedChunks instanceof Array)
      ? data.receivedChunks.slice().sort(function(a, b) { return a - b; })
      : item.receivedChunks;

    if (item.totalChunks > 0) {
      item.progressPct = Math.min(100, (item.receivedChunks.length / item.totalChunks) * 100);
    }
  }

  async function completeUpload(item) {
    var record = await httpJson("/files/upload/complete/" + encodeURIComponent(item.uploadId), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ displayName: item.file.name })
    });

    item.record = record;
    item.progressPct = 100;
    item.status = "complete";

    var uae = dom.closest("uae");
    if (uae) {
      uae.setAttribute("data-uploaded-uri", record.uri || "");
      uae.setAttribute("data-uploaded-file-id", record.id || "");
      uae.setAttribute("data-uploaded-file-mime", record.mimeType || "");
    }

    dom.dispatchEvent(new CustomEvent("ua:fileupload:complete", {
      bubbles: true,
      detail: {
        controlName: options.name,
        record: record,
        item: {
          id: item.id,
          fileName: item.file.name,
          secure: item.secure,
          folder: item.folder || "",
          scope: item.scope || ""
        }
      }
    }));

    if (item.secure) await showSecureDialog(record, state.options || {});
  }

  function nextChunkIndex(item) {
    var seen = {};
    for (var i = 0; i < item.receivedChunks.length; i++) seen[item.receivedChunks[i]] = true;
    for (var j = 0; j < item.totalChunks; j++) {
      if (!(seen[j])) return j;
    }
    return item.totalChunks;
  }

  async function processItem(item) {
    try {
      if (!(item.uploadId)) await requestUploadSession(item);
      else await syncUploadStatus(item);

      item.status = "uploading";
      item.pauseRequested = false;
      renderQueue();

      var idx = nextChunkIndex(item);
      while (idx < item.totalChunks) {
        if (item.cancelled) {
          item.status = "cancelled";
          renderQueue();
          return;
        }

        if (item.pauseRequested) {
          item.status = "paused";
          renderQueue();
          return;
        }

        var start = idx * item.chunkMaxBytes;
        var end = Math.min(item.file.size, start + item.chunkMaxBytes);
        var chunk = item.file.slice(start, end);

        try {
          await uploadChunk(item, idx, chunk);
        } catch (error) {
          if (isConnectionError(error)) {
            item.status = "lost";
            renderQueue();
            return;
          }

          item.status = "error";
          item.message = (error && error.message) || "Upload failed.";
          renderQueue();
          return;
        }

        idx = nextChunkIndex(item);
        renderQueue();
      }

      await completeUpload(item);
      renderQueue();
    } catch (error) {
      console.error("ua.file.upload item processing failed", error);
      if (isConnectionError(error)) item.status = "lost";
      else {
        item.status = "error";
        item.message = (error && error.message) || "Upload failed.";
      }
      renderQueue();
    }
  }

  async function processQueue() {
    if (state.processing) return;
    state.processing = true;
    try {
      while (true) {
        var next = null;
        for (var i = 0; i < state.items.length; i++) {
          if (state.items[i].status === "queued") {
            next = state.items[i];
            break;
          }
        }
        if (!(next)) break;
        await processItem(next);
      }
    } finally {
      state.processing = false;
    }

    var hasQueued = state.items.some(function(item) {
      return item.status === "queued" || item.status === "uploading";
    });

    if (!hasQueued) {
      dom.dispatchEvent(new CustomEvent("ua:fileupload:batchcomplete", {
        bubbles: true,
        detail: {
          controlName: options.name,
          items: state.items.map(function(item) {
            return {
              id: item.id,
              fileName: item.file.name,
              status: item.status,
              record: item.record || null,
              folder: item.folder || "",
              scope: item.scope || "",
              message: item.message || ""
            };
          })
        }
      }));
    }

    if (!continuousWork) {
      var hasActiveOrIssue = state.items.some(function(item) {
        return item.status === "uploading" || item.status === "queued" || item.status === "paused" || item.status === "lost";
      });

      if (!hasActiveOrIssue) {
        setTimeout(function() {
          state.items = [];
          renderQueue();
        }, 800);
      }
    }
  }

  async function cancelItem(item) {
    if (!(item)) return;
    item.cancelled = true;
    item.pauseRequested = false;

    if (item.uploadId) {
      try {
        await httpJson("/files/upload/cancel/" + encodeURIComponent(item.uploadId), {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: "{}"
        });
      } catch (error) {
        console.warn("ua.file.upload cancel request failed", error);
      }
    }

    item.status = "cancelled";
    renderQueue();
  }

  function getItem(id) {
    var itemId = (id || "").toString().trim();
    for (var i = 0; i < state.items.length; i++) {
      if (state.items[i].id === itemId) return state.items[i];
    }
    return null;
  }

  async function openMainPicker() {
    if (!(allowFolders)) {
      if (fileInput && fileInput.click) fileInput.click();
      return;
    }

    if (window && typeof window.showOpenFilePicker === "function" && typeof window.showDirectoryPicker === "function") {
      try {
        var fileHandles = await window.showOpenFilePicker({ multiple: !!allowMultipleFiles });
        var selectedFiles = [];
        for (var i = 0; i < fileHandles.length; i++) {
          selectedFiles.push(await fileHandles[i].getFile());
        }
        if (selectedFiles.length > 0) {
          addFilesToQueue(selectedFiles);
          return;
        }
      } catch (filePickError) {
        if (filePickError && filePickError.name !== "AbortError") {
          console.warn("ua.file.upload: open file picker failed", filePickError);
        }
      }

      try {
        var folderHandle = await window.showDirectoryPicker();
        var filesFromFolder = await readDirectoryFiles(folderHandle, folderHandle.name || "");
        if (filesFromFolder.length > 0) {
          addFilesToQueue(filesFromFolder);
          return;
        }
      } catch (folderPickError) {
        if (folderPickError && folderPickError.name !== "AbortError") {
          console.warn("ua.file.upload: open folder picker failed", folderPickError);
        }
      }
    }

    if (allowFolders && folderInput && folderInput.tagName === "INPUT" && folderInput.click) {
      var chooseFiles = window.confirm("Upload files? Choose OK for files, or Cancel to pick a folder.");
      if (chooseFiles && fileInput && fileInput.click) fileInput.click();
      else folderInput.click();
      return;
    }

    if (fileInput && fileInput.click) fileInput.click();
  }

  function bindDragDrop() {
    if (!allowDragDropFiles) return;

    function shouldCaptureWindowDrop() {
      if (!allowWindowDropFiles) return false;
      var uploaders = document.querySelectorAll("[data-ua-file-upload]");
      return uploaders.length === 1;
    }

    var onDragOverWindow = function(event) {
      if (!hasFilePayload(event)) return;
      if (shouldCaptureWindowDrop()) {
        event.preventDefault();
        dragState.hoverOnUploader = true;
        updateOverlayVisible(true);
        return;
      }
      var target = document.elementFromPoint(event.clientX, event.clientY);
      dragState.hoverOnUploader = !!(target && dom.contains(target));
      updateOverlayVisible(dragState.hoverOnUploader);
      if (dragState.hoverOnUploader) event.preventDefault();
    };

    var onDragLeaveWindow = function() {
      dragState.hoverOnUploader = false;
      updateOverlayVisible(false);
    };

    var onDropWindow = function(event) {
      if (!hasFilePayload(event)) return;
      if (shouldCaptureWindowDrop()) {
        updateOverlayVisible(false);
        dragState.hoverOnUploader = false;
        event.preventDefault();
        var winFiles = event.dataTransfer && event.dataTransfer.files;
        if (winFiles && winFiles.length > 0) addFilesToQueue(winFiles);
        return;
      }
      var target = document.elementFromPoint(event.clientX, event.clientY);
      var onThisUploader = !!(target && dom.contains(target));
      updateOverlayVisible(false);
      dragState.hoverOnUploader = false;

      if (!onThisUploader) return;
      event.preventDefault();

      var files = event.dataTransfer && event.dataTransfer.files;
      if (files && files.length > 0) addFilesToQueue(files);
    };

    window.addEventListener("dragover", onDragOverWindow);
    window.addEventListener("dragleave", onDragLeaveWindow);
    window.addEventListener("drop", onDropWindow);
  }

  queueHost.addEventListener("click", async function(event) {
    var btn = event.target.closest("button[data-upload-item-action]");
    if (!(btn)) return;

    var action = (btn.getAttribute("data-upload-item-action") || "").toString().trim().toLowerCase();
    var item = getItem(btn.getAttribute("data-upload-item-id"));
    if (!(item)) return;

    if (action === "cancel") {
      await cancelItem(item);
      return;
    }

    if (action === "pause") {
      item.pauseRequested = true;
      item.status = "paused";
      renderQueue();
      return;
    }

    if (action === "resume") {
      item.pauseRequested = false;
      item.cancelled = false;
      if (item.status === "paused" || item.status === "lost") item.status = "queued";
      renderQueue();
      processQueue();
    }
  });

  if (privacyToggleBtn) {
    privacyToggleBtn.addEventListener("click", function() {
      state.secure = !state.secure;
      updatePrivacyVisual();
    });
  }

  if (selectMainBtn) {
    selectMainBtn.addEventListener("click", async function() {
      await openMainPicker();
    });
  }

  if (fileInput) {
    fileInput.addEventListener("change", function() {
      var files = fileInput.files;
      if (files && files.length > 0) addFilesToQueue(files);
      fileInput.value = "";
    });
  }

  if (folderInput && folderInput.tagName === "INPUT") {
    folderInput.addEventListener("change", function() {
      var files = folderInput.files;
      if (files && files.length > 0) addFilesToQueue(files);
      folderInput.value = "";
    });
  }

  dom.addEventListener("ua:fileupload:setfolder", function(event) {
    var detail = (event || {}).detail || {};
    setCurrentFolder(detail.folder || "");
  });

  updatePrivacyVisual();
  setCurrentFolder(folderDefault);
  bindDragDrop();
  renderQueue();
  return dom;
};
